import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../components/DashboardLayout/DasboardLayout";
import UserDetailsSidebar from "./userDetailSide";
import { User, AlertCircle, Send, Ticket, RefreshCw, CheckCircle, Mail, Phone } from "lucide-react";
import { API_URL } from "../components/config";
import AutoAssignmentService from "./AutoAssignmentService";

const ChatScreen = () => {
  const { id, customerid } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const adminId = localStorage.getItem("adminId");
  const [userDetails, setUserDetails] = useState(null);
  const [activeTab, setActiveTab] = useState("userDetails");
  const [description, setDescription] = useState("");
  const [ticketCreated, setTicketCreated] = useState(false);
  const [ticketAssigned, setTicketAssigned] = useState(false);
  const messagesEndRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const [ticketPriority, setTicketPriority] = useState("1");
  const currentAgentId = localStorage.getItem("adminId") || localStorage.getItem("userid") || localStorage.getItem("userId");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    refreshData();
  }, [customerid, id]);

  const refreshData = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchUserDetails();
      await fetchMessages();
    } catch (err) {
      setError("Failed to refresh data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async () => {
    try {
      let response;
      if (customerid) {
        response = await axios.get(`${API_URL}/customerdetails/${customerid}`);
        if (response.data && response.data.length > 0) {
          setUserDetails(response.data[0]);
          return;
        }
      }
      response = await axios.get(`${API_URL}/driver/${customerid}`);
      if (response.data && response.data.length > 0) {
        setUserDetails(response.data[0]);
      } else {
        setError("No user details found for the provided ID.");
      }
    } catch (err) {
      setError("Failed to fetch user details.");
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/clientservicechat/getchatsbychatid/${id}`);
      setMessages(response.data);
    } catch (err) {
      setError("Failed to fetch messages.");
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setIsTyping(true);
    const dateChat = new Date().toISOString().split("T")[0];
    const timeChat = new Date().toLocaleTimeString();

    try {
      await axios.post(`${API_URL}/customer_admin_chats`, {
        customer_driver_chat_id: null,
        date_chat: dateChat,
        time_chat: timeChat,
        trip_id: "0",
        admin_id: adminId,
        driver_id: customerid,
        message: newMessage,
        conversation_id: id,
        origin: adminId,
      });

      setNewMessage("");
      fetchMessages();
    } catch (err) {
      setError("Failed to send your message.");
    } finally {
      setIsTyping(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!userDetails) {
      setError("User details are not available.");
      return;
    }

    const currentDate = new Date().toISOString();
    const expiryDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();

    try {
      // 1. Create the ticket
      const ticketResponse = await axios.post(`${API_URL}/tickets`, {
        customer_id: customerid,
        customer_name: userDetails.name,
        phone1: userDetails.phone,
        phone2: userDetails.phone2 || "",
        email: userDetails.email,
        surname: userDetails.surname || "",
        company: "xgo",
        product: "xgo",
        conversation_stage: "new",
        person_assigned: "n/A",
        expiry_date_time: expiryDate,
        priority_level: parseInt(ticketPriority),
        created_at: currentDate,
        estimation_duration: "3 days",
        description: description,
        chat_id: id,
        department: "Support",
        category: "general", // Default category for conversation-triggered tickets
      });

      const newTicket = ticketResponse.data;
      console.log("Auto-assign - New ticket created:", newTicket);
      
      // Extract the ticket ID from the response structure
      const ticketId = newTicket.result?.insertId || newTicket.conversation_support_id;
      console.log("Auto-assign - Ticket ID:", ticketId);
      
      // Check if the response has the expected structure
      if (!newTicket || !ticketId) {
        console.error("Auto-assign - Invalid ticket response:", newTicket);
        setError("Failed to create ticket - invalid response from server");
        return;
      }

      // 2. TRIGGER AUTO-ASSIGNMENT IMMEDIATELY
      const assignmentResult = await AutoAssignmentService.autoAssignTicket(
        ticketId,
        "general" // Default category for conversation tickets
      );

      if (assignmentResult && assignmentResult.success) {
        // 3. SUCCESS - Ticket auto-assigned
        setDescription("");
        setTicketCreated(true);
        
        // Show success with assignment info
        AutoAssignmentService.showSuccessToast(
          "Ticket Created & Auto-Assigned!",
          `Ticket #${ticketId} assigned to ${assignmentResult.agent_name}`
        );
        
      } else {
        // 4. FALLBACK - No agents available, manual assignment needed
        setDescription("");
        setTicketCreated(true);
        
        AutoAssignmentService.showWarningToast(
          "Ticket Created - Manual Assignment Required",
          `Ticket #${ticketId} created. ${assignmentResult?.reason || 'Please assign manually.'}`
        );
      }

    } catch (err) {
      setError("Failed to create ticket.");
      console.error("Ticket creation error:", err);
    }
  };

  const handleCreateAndAssignTicket = async (e) => {
    e.preventDefault();
    if (!userDetails) {
      setError("User details are not available.");
      return;
    }

    const currentDate = new Date().toISOString();
    const expiryDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();

    try {
      // 1. Create the ticket
      console.log("Creating ticket with data:", {
        customer_id: customerid,
        customer_name: userDetails.name,
        phone1: userDetails.phone,
        phone2: userDetails.phone2 || "",
        email: userDetails.email,
        surname: userDetails.surname || "",
        company: "xgo",
        product: "xgo",
        conversation_stage: "new",
        person_assigned: "n/A",
        expiry_date_time: expiryDate,
        priority_level: parseInt(ticketPriority),
        created_at: currentDate,
        estimation_duration: "3 days",
        description: description,
        chat_id: id,
        department: "Support",
        category: "general", // Default category for conversation-triggered tickets
      });
      
      const ticketResponse = await axios.post(`${API_URL}/tickets`, {
        customer_id: customerid,
        customer_name: userDetails.name,
        phone1: userDetails.phone,
        phone2: userDetails.phone2 || "",
        email: userDetails.email,
        surname: userDetails.surname || "",
        company: "xgo",
        product: "xgo",
        conversation_stage: "new",
        person_assigned: "n/A",
        expiry_date_time: expiryDate,
        priority_level: parseInt(ticketPriority),
        created_at: currentDate,
        estimation_duration: "3 days",
        description: description,
        chat_id: id,
        department: "Support",
        category: "general", // Default category for conversation-triggered tickets
      });

      const newTicket = ticketResponse.data;
      console.log("New ticket created:", newTicket);
      
      // Extract the ticket ID from the response structure
      const ticketId = newTicket.result?.insertId || newTicket.conversation_support_id;
      console.log("Ticket ID:", ticketId);
      
      // Check if the response has the expected structure
      if (!newTicket || !ticketId) {
        console.error("Invalid ticket response:", newTicket);
        setError("Failed to create ticket - invalid response from server");
        return;
      }

      // 2. ASSIGN TICKET DIRECTLY TO CURRENT AGENT
      if (currentAgentId) {
        // Assign directly to current agent
        await axios.put(`${API_URL}/tickets/${ticketId}`, {
          person_assigned: currentAgentId,
          conversation_stage: "assigned"
        });
        
        // SUCCESS - Ticket created and assigned to current agent
        setDescription("");
        setTicketAssigned(true);
        
        // Get current agent name for success message
        const agentName = localStorage.getItem("username") || "Current Agent";
        
        AutoAssignmentService.showSuccessToast(
          "Ticket Created & Assigned!",
          `Ticket #${ticketId} assigned to you (${agentName})`
        );
      } else {
        // No admin ID found - create without assignment
        setDescription("");
        setTicketAssigned(true);
        
        AutoAssignmentService.showWarningToast(
          "Ticket Created - No Assignment",
          `Ticket #${ticketId} created. Please assign manually.`
        );
      }

    } catch (err) {
      setError("Failed to create ticket.");
      console.error("Ticket creation error:", err);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex h-screen bg-gray-50">
        <div className="flex-grow flex flex-col p-6">
          {/* Chat Header */}
          <div className="bg-white rounded-t-xl shadow-sm p-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full bg-[#ffc000] flex items-center justify-center">
                <User className="text-[#002966]" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-[#002966]">
                  {userDetails?.name || 'Loading...'}
                </h2>
                <p className="text-sm text-gray-500">Chat ID: {id}</p>
              </div>
            </div>
            {isTyping && (
              <div className="text-sm text-gray-500 animate-pulse">
                Admin is typing...
              </div>
            )}
            <button
              onClick={refreshData}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Refresh
            </button>
          </div>

          {/* Chat Messages */}
          <div className="flex-grow bg-white shadow-sm overflow-y-auto p-6">
            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-4 flex items-center">
                <AlertCircle size={20} className="mr-2" />
                {error}
              </div>
            )}
            
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={`${msg.conversation_id}-${msg.date_chat}-${index}`}
                  className={`flex ${msg.origin == adminId ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl p-4 ${
                      msg.origin == adminId
                        ? "bg-[#002966] text-white"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    <p className="text-sm">{msg.message}</p>
                    <p className={`text-xs mt-2 ${
                      msg.origin == adminId ? "text-blue-200" : "text-gray-500"
                    }`}>
                      {new Date(msg.date_created).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Message Input */}
          <form onSubmit={handleReply} className="bg-white rounded-b-xl shadow-sm p-4">
            <div className="flex space-x-4">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-grow px-4 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-[#ffc000] focus:border-transparent"
              />
              <button
                type="submit"
                className="bg-[#002966] text-white px-6 py-2 rounded-full hover:bg-[#001f4d] transition-colors duration-300 flex items-center"
              >
                <Send size={20} className="mr-2" />
                Send
              </button>
            </div>
          </form>
        </div>

        {/* Sidebar */}
        <div className="w-96 bg-white border-l border-gray-200 p-6">
          <div className="flex mb-6 bg-gray-100 rounded-lg">
            <button
              onClick={() => setActiveTab("userDetails")}
              className={`flex-1 p-3 rounded-lg transition-colors duration-300 ${
                activeTab === "userDetails"
                  ? "bg-[#002966] text-white"
                  : "text-gray-600"
              }`}
            >
              User Details
            </button>
            <button
              onClick={() => setActiveTab("createTicket")}
              className={`flex-1 p-3 rounded-lg transition-colors duration-300 ${
                activeTab === "createTicket"
                  ? "bg-[#002966] text-white"
                  : "text-gray-600"
              }`}
              disabled={ticketCreated || ticketAssigned}
            >
              Create Ticket
            </button>
          </div>

          {activeTab === "userDetails" && userDetails && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-[#002966] mb-4">
                  Customer Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center text-gray-600">
                    <User size={18} className="mr-3" />
                    <span>{userDetails.name}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Mail size={18} className="mr-3" />
                    <span>{userDetails.email}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Phone size={18} className="mr-3" />
                    <span>{userDetails.phone}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "createTicket" && !ticketCreated && !ticketAssigned && (
            <div className="space-y-4">
              <form onSubmit={handleCreateTicket} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority Level
                  </label>
                  <select
                    value={ticketPriority}
                    onChange={(e) => setTicketPriority(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg p-2 focus:ring-2 focus:ring-[#ffc000] focus:border-transparent"
                  >
                    <option value="1">High</option>
                    <option value="2">Medium</option>
                    <option value="3">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter ticket description..."
                    className="w-full h-32 border border-gray-200 rounded-lg p-3 focus:ring-2 focus:ring-[#ffc000] focus:border-transparent"
                    required
                  />
                </div>
                
                <div className="space-y-3">
                  <button
                    type="submit"
                    className="w-full bg-[#002966] text-white py-3 rounded-lg hover:bg-[#001f4d] transition-colors duration-300 flex items-center justify-center"
                  >
                    <Ticket size={20} className="mr-2" />
                    Create Ticket (Auto-Assign)
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleCreateAndAssignTicket}
                    className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors duration-300 flex items-center justify-center"
                  >
                    <Ticket size={20} className="mr-2" />
                    Create and Assign Me Ticket
                  </button>
                </div>
              </form>
            </div>
          )}

          {ticketCreated && (
            <div className="bg-green-50 p-4 rounded-lg flex items-center">
              <CheckCircle className="text-green-500 mr-2" size={20} />
              <p className="text-green-700">Ticket has been created successfully!</p>
            </div>
          )}

          {ticketAssigned && (
            <div className="bg-green-50 p-4 rounded-lg flex items-center">
              <CheckCircle className="text-green-500 mr-2" size={20} />
              <p className="text-green-700">Ticket has been created and assigned to you!</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ChatScreen;
